<template>
  <div class="i-careers">
    <section>
      <div
        id="overview"
        class="tag"
      >
        <iCareerOverview />
      </div>

      <div
        v-if="GlobalFunctions.isExtendedRecommendedJobType(activeJob) && !!activeJob?.matchScore"
        id="compatibility"
        class="tag"
      >
        <iBaseHeader
          highlight-text="Compatibility"
          :version="2"
          font-size-highlighted-text="16px"
          text=""
          align="start"
        />
        <iBaseHeader
          highlight-text=""
          text="Why am I a good match?"
          align="start"
        />
        <iWhyMatched />
      </div>

      <div
        id="insights"
        class="tag"
      >
        <iBaseHeader
          highlight-text="Insights"
          :version="2"
          font-size-highlighted-text="16px"
          text=""
          align="start"
        />
        <iBaseHeader
          highlight-text=""
          :text="`What it's like being ${jobTitleWithArticle}?`"
          align="start"
        />
        <iCareerResponsibilities />
        <iMedianSalaries />
      </div>

      <div
        v-if="data.fetchedJobListings"
        id="job_opportunities"
        class="tag"
      >
        <iBaseHeader
          highlight-text="Job Opportunities"
          :version="2"
          font-size-highlighted-text="16px"
          text=""
          align="start"
        />
        <iBaseHeader
          v-if="data.jobListings.length > 0"
          highlight-text=""
          :text="`Find ${activeJob?.title} roles in your area`"
          align="start"
        />
        <template v-if="data.jobListings.length > 0">
          <iAdzunaJobBoard
            :job-listings="data.jobListings"
            :job-id="reportStore.activeJobId"
          />
          <iBaseButton
            class="google-button"
            :pre-icon="iconGoogle"
            v-bind="MODAL_BUTTON_STYLE"
            @clicked="goToJobOpenings"
          />
        </template>
        <iActionCard
          v-else
          :image-url="imageOnlineCoaching"
          :text="`See what ${activeJob?.title} positions are available near you.`"
          cta-text="See Job Listings"
          :pre-icon="iconGoogle"
          :style="{ marginBottom: '40px' }"
          @cta-clicked="goToJobOpenings"
        />
      </div>

      <div
        id="what_people_say"
        class="tag"
      >
        <iBaseHeader
          highlight-text="What people say"
          :version="2"
          font-size-highlighted-text="16px"
          text=""
          align="start"
        />
        <iBaseHeader
          highlight-text=""
          :text="`From actual ${getPluralJob(activeJob?.title || '')}`"
          align="start"
        />
        <iCareerCharacteristics v-if="userStore.isTierComprehensiveOrHigher" />
        <iWhatPeopleSay />
      </div>
    </section>

    <section
      id="qualifications"
      class="tag"
    >
      <iBaseHeader
        highlight-text="Qualifications"
        :version="2"
        font-size-highlighted-text="16px"
        text=""
        align="start"
      />
      <iBaseHeader
        highlight-text=""
        :text="`Your roadmap to becoming ${jobTitleWithArticle}`"
        align="start"
        :print-break-before="true"
      />
      <iCareerRequirements />
    </section>

    <section>
      <iCareerSkillset />
      <iActionCard
        :image-url="imageSmilingMentee"
        :text="secondActionCardText"
      />
    </section>

    <section
      v-if="activeJobMythFact.length > 0"
      id="myths"
      class="tag"
    >
      <iBaseHeader
        highlight-text="Misconceptions"
        :version="2"
        font-size-highlighted-text="16px"
        text=""
        align="start"
      />
      <iMythsAndFacts />
    </section>

    <section
      v-if="!userStore.isTierPremium && !userStore.isActiveReportSubscriber"
      id="need_help"
      class="print-hide tag"
    >
      <iBaseHeader
        highlight-text="Not sure on what to do next?"
        :font-size-highlighted-text="headerSize"
        text="Talk to our career coach to figure out what exactly you need to do."
        font-size-text="14px"
        font-weight-text="500"
        :color-text="comet600"
        align="start"
      />
      <iCoachingUpsell />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, onMounted, reactive, watch } from "vue"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { azureRadiance600, comet600, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import getPluralJob from "@/composables/getPluralJob"
import { getArticle } from "@/composables/getArticle"
import { GlobalFunctions } from "@/helpers/globalFunctions"
import api from "@/services/apiClient"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseHeader from "@/components/base/BaseHeader.vue"
import iActionCard from "@/components/global/ActionCard.vue"
import iCareerOverview from "@/components/report/careers/CareerOverview.vue"
import iMedianSalaries from "@/components/report/careers/MedianSalaries.vue"
import iCareerResponsibilities from "@/components/report/careers/CareerResponsibilities.vue"
import iCareerCharacteristics from "@/components/report/careers/CareerCharacteristics.vue"
import iAdzunaJobBoard from "@/components/report/careers/AdzunaJobBoard.vue"
import iWhatPeopleSay from "@/components/report/careers/WhatPeopleSay.vue"
import iCareerRequirements from "@/components/report/careers/CareerRequirements.vue"
import iCareerSkillset from "@/components/report/careers/CareerSkillset.vue"
import iMythsAndFacts from "@/components/report/careers/MythsAndFacts.vue"
import iWhyMatched from "@/components/report/careers/WhyMatched.vue"
import iCoachingUpsell from "@/components/report/careers/CoachingUpsell.vue"

//ASSETS
import iconGoogle from "@/assets/icons/icon-google-transparent.svg"
import imageOnlineCoaching from "@/assets/careers/career-action-1.png"
import imageSmilingMentee from "@/assets/careers/career-action-3.png"
import iconSummary from "@/assets/icons/icon-summary.svg"
import iconSummaryBlue from "@/assets/icons/icon-summary-blue.svg"
import iconCompatibility from "@/assets/anchors/icon-user-check.svg"
import iconCompatibilityActive from "@/assets/anchors/icon-user-check-active.svg"
import iconInsight from "@/assets/icons/icon-eye.svg"
import iconInsightBlue from "@/assets/icons/icon-eye-blue.svg"
import iconPeopleSay from "@/assets/anchors/icon-face-happy.svg"
import iconPeopleSayActive from "@/assets/anchors/icon-face-happy-active.svg"
import iconJobOpportunities from "@/assets/anchors/icon-briefcase.svg"
import iconJobOpportunitiesActive from "@/assets/anchors/icon-briefcase-active.svg"
import iconAward from "@/assets/anchors/icon-award.svg"
import iconAwardActive from "@/assets/anchors/icon-award-active.svg"
import iconAsk from "@/assets/icons/icon-message-question-circle.svg"
import iconAskBlue from "@/assets/icons/icon-message-question-circle-blue.svg"
import iconNeedHelp from "@/assets/icons/icon-info-circle.svg"
import iconNeedHelpBlue from "@/assets/icons/icon-info-circle-blue.svg"

// TYPES
import type { PageAnchors } from "@/types/pageAnchors"
import { JobListing } from "@/shared/dtos/out/JobGetListingsOut"
import { ExtendedRecommendedJob } from "@/types"

// EMITS
const emit = defineEmits(["load-anchors"])

// STORES AND COMPOSABLES
const reportStore = useReportStore()
const userStore = useUserStore()
const isNotMobile = useIsScreenSize(breakpointSm)

// CONSTANTS
const MODAL_BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "fit-content",
  backgroundColor: azureRadiance600,
  borderRadius: "8px",
  textColor: white,
  text: "See More Job Listings"
}

// DATA
const data = reactive({
  jobListings: [] as JobListing[],
  fetchedJobListings: false
})

// COMPUTED
const constantPageAnchors: ComputedRef<PageAnchors> = computed(() => {
  return {
    overview: {
      text: "Overview",
      icon: iconSummary,
      iconActive: iconSummaryBlue
    },
    compatibility: {
      text: "Compatibility",
      icon: iconCompatibility,
      iconActive: iconCompatibilityActive,
      isHidden: !(activeJob.value as unknown as ExtendedRecommendedJob)?.matchScore
    },
    insights: {
      text: "Insights",
      icon: iconInsight,
      iconActive: iconInsightBlue
    },
    job_opportunities: {
      text: "Job Opportunities",
      icon: iconJobOpportunities,
      iconActive: iconJobOpportunitiesActive,
      isHidden: !data.fetchedJobListings
    },
    what_people_say: {
      text: "What People Say",
      icon: iconPeopleSay,
      iconActive: iconPeopleSayActive
    },
    qualifications: {
      text: "Qualifications",
      icon: iconAward,
      iconActive: iconAwardActive
    },
    myths: {
      text: "Misconceptions",
      icon: iconAsk,
      iconActive: iconAskBlue
    },
    need_help: {
      text: "Need Help?",
      icon: iconNeedHelp,
      iconActive: iconNeedHelpBlue,
      isHidden: userStore.isTierPremium || userStore.isActiveReportSubscriber
    }
  } as PageAnchors
})

const headerSize = computed(() => (isNotMobile.value ? "36px" : "32px"))

const activeJob = computed(() => reportStore.getActiveJob)

const activeJobSearchLink = computed(() => {
  const job = activeJob.value?.title?.replace(/ /g, "+")
  return `https://www.google.com/search?q=${job}&ibp=htl;jobs#htivrt=jobs`
})

const secondActionCardText = computed(
  () =>
    `${activeJob.value?.characteristics?.satisfaction ? `${activeJob.value?.characteristics?.satisfaction}%` : "A lot"}  of people report higher satisfaction after making this <span class='highlighted'>career change.</span>`
)

const jobTitleWithArticle = computed(() => `${getArticle(activeJob.value?.title || "")} ${activeJob.value?.title}`)

const activeJobMythFact = computed(() => {
  return reportStore.getActiveJobMythFact
})

// FUNCTIONS
const fetchJobListings = async () => {
  try {
    const jobListings = await api.job.getJobListings(reportStore.activeJobId)
    data.jobListings = jobListings.listings || []
  } catch (err) {
    console.error("Failed to fetch adzuna job listings", err)
  }
  data.fetchedJobListings = true
}

const goToJobOpenings = () => {
  window.open(activeJobSearchLink.value, "_blank")
}

// LIFECYCLE HOOKS
onMounted(() => {
  fetchJobListings()
  emit("load-anchors", { anchors: constantPageAnchors.value, autoScroll: true })
})

// WATCHERS
watch(
  () => data.jobListings,
  () => emit("load-anchors", { anchors: constantPageAnchors.value, autoScroll: false })
)
</script>

<style scoped lang="scss">
.i-careers {
  max-width: 720px;
  margin: 0 auto;

  @include flex($direction: column, $justify: start, $align: stretch, $gap: 40px);

  .tag {
    scroll-margin-top: 90px;
  }

  .google-button {
    margin: 0 auto 40px;
  }
}

@media print {
  .i-careers {
    gap: 20px;

    .print-hide {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.i-careers {
  .no-mobile-break {
    margin-top: 32px;

    br {
      display: none;

      @media screen and (min-width: $breakpoint-sm) {
        display: block;
      }
    }
  }
}
</style>
